import React from "react";
import { Link } from "react-router-dom";

/**
 * The Trellure mark: a book spine with sound radiating from it.
 *
 * The upright bar is the spine and the stem of the T; the three arcs are the
 * voice coming off the page, widening as they travel. Drawn as strokes rather
 * than fills so it stays legible at 16px in a browser tab.
 */
export function LogoMark({ className = "w-5 h-5", strokeWidth = 2 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
    >
      {/* spine */}
      <path d="M8 4.5v15" stroke="currentColor" />
      {/* crossbar of the T */}
      <path d="M4.5 4.5h7" stroke="currentColor" />
      {/* the voice, three arcs opening to the right */}
      <path d="M13.5 9a5 5 0 0 1 0 6" stroke="currentColor" opacity="0.9" />
      <path d="M16.5 6.5a9 9 0 0 1 0 11" stroke="currentColor" opacity="0.6" />
      <path d="M19.5 4.5a13 13 0 0 1 0 15" stroke="currentColor" opacity="0.32" />
    </svg>
  );
}

/**
 * Full lockup: mark in a brand-gradient disc plus the wordmark.
 * `size` covers the two places it appears at different scales.
 */
export default function Logo({ size = "md", to = "/", className = "" }) {
  const s = size === "sm"
    ? { disc: "w-8 h-8", mark: "w-4 h-4", text: "text-xl" }
    : { disc: "w-9 h-9", mark: "w-5 h-5", text: "text-2xl" };

  const inner = (
    <>
      <div
        className={`relative ${s.disc} rounded-full bg-gradient-to-br from-brand-300 to-brand-600 flex items-center justify-center shadow-lg shadow-brand-500/20`}
      >
        <LogoMark className={`${s.mark} text-brand-950`} strokeWidth={2.2} />
      </div>
      <span className={`font-display ${s.text} font-semibold tracking-tight`}>
        Trell<span className="brand-text">ure</span>
      </span>
    </>
  );

  if (!to) return <div className={`flex items-center gap-2 ${className}`}>{inner}</div>;
  return (
    <Link to={to} className={`flex items-center gap-2 shrink-0 group ${className}`}>
      {inner}
    </Link>
  );
}
